/**
 * CMSSyncTab - Main tab component for CMS entity synchronization.
 * Displays Filevine collection data and allows triggering sync operations.
 * Supports multiple entity types via tabs (Medical Providers, Coverage).
 */

import { formatDistanceToNow, parseISO } from 'date-fns';
import { AlertCircle, Clock, RefreshCw } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ENTITY_SYNC_TYPES,
  useEntitySyncFieldConfigQuery,
  useEntitySyncListPairsQuery,
} from '@/services/api/entitySyncService';
import type { DetailedFilevineCollectionItemSchema } from '@/services/api/generated/entitySyncService';

import useMatterSelection from '../../hooks/useMatterSelection';
import { EntityLinkingSection } from './entity-linking';
import { PollingProvider } from './entity-linking/context/PollingContext';
import type { CanonicalSectionType } from './entity-linking/finch-cards';
import { SyncSuggestionCardList } from './entity-linking/sync-suggestions';
import { FullPipelineButton } from './FullPipelineButton';

/**
 * Entity type tab configuration
 */
const ENTITY_TABS: { value: CanonicalSectionType; label: string }[] = [
  { value: ENTITY_SYNC_TYPES.MEDICAL_PROVIDERS as CanonicalSectionType, label: 'Medical Providers' },
  { value: ENTITY_SYNC_TYPES.COVERAGE as CanonicalSectionType, label: 'Coverage' },
];

/**
 * Section header with title and pipeline button
 */
const SectionHeader: React.FC<{
  sectionName: string;
  itemCount: number;
  lastSyncTime: string | null;
  matterId: string;
  entityType: string;
  onPipelineComplete: () => void;
}> = ({
  sectionName,
  itemCount,
  lastSyncTime,
  matterId,
  entityType,
  onPipelineComplete,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-zinc-900">
              {sectionName}
            </h2>
            <Badge variant="secondary" className="font-normal">
              {itemCount} item{itemCount !== 1 ? 's' : ''}
            </Badge>
          </div>
          {lastSyncTime && (
            <p className="text-sm text-zinc-500 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Last synced {lastSyncTime}
            </p>
          )}
        </div>

        <FullPipelineButton
          matterId={matterId}
          entityType={entityType}
          onPipelineComplete={onPipelineComplete}
        />
      </div>
    </div>
  );
};

/**
 * Loading skeleton for the entire tab
 */
const TabSkeleton: React.FC = () => (
  <div className="flex flex-col h-full">
    <div className="border-b border-zinc-200 p-6">
      <Skeleton className="h-8 w-32 mb-2" />
      <Skeleton className="h-4 w-64" />
    </div>
    <div className="flex-1 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-10 w-28" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    </div>
  </div>
);

/**
 * Error state when API calls fail
 */
const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-6">
    <div className="rounded-full bg-red-100 p-4 mb-4">
      <AlertCircle className="h-8 w-8 text-red-500" />
    </div>
    <h2 className="text-lg font-medium text-zinc-900 mb-2">
      Failed to load data
    </h2>
    <p className="text-sm text-zinc-500 max-w-md mb-4">{message}</p>
    <Button onClick={onRetry} variant="outline">
      <RefreshCw className="h-4 w-4 mr-2" />
      Try Again
    </Button>
  </div>
);

/**
 * Not configured state when matter doesn't have Filevine integration
 */
const NotConfiguredState: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-full text-center p-6">
    <div className="rounded-full bg-amber-100 p-4 mb-4">
      <AlertCircle className="h-8 w-8 text-amber-500" />
    </div>
    <h2 className="text-lg font-medium text-zinc-900 mb-2">
      Filevine Not Configured
    </h2>
    <p className="text-sm text-zinc-500 max-w-md">
      This matter is not linked to a Filevine project, or the firm does not have
      a Filevine organization configured. Please set up the Filevine integration
      to sync collection data.
    </p>
  </div>
);

/**
 * Content for a single entity type tab
 */
const EntitySyncContent: React.FC<{
  matterId: string;
  firmId?: string;
  entityType: CanonicalSectionType;
}> = ({ matterId, firmId, entityType }) => {
  const {
    data: fieldConfig,
    isLoading: fieldConfigLoading,
    error: fieldConfigError,
    refetch: refetchFieldConfig,
  } = useEntitySyncFieldConfigQuery(
    { matterId, entityType },
    { skip: !matterId },
  );

  const {
    data: pairsData,
    isLoading: itemsLoading,
    error: itemsError,
    refetch: refetchItems,
  } = useEntitySyncListPairsQuery(
    { matterId, entityType, includeUnlinked: true },
    { skip: !matterId },
  );

  // Extract Filevine items from pairs
  const items: DetailedFilevineCollectionItemSchema[] = React.useMemo(
    () =>
      (pairsData?.pairs ?? [])
        .filter((pair) => pair.filevine_entity != null)
        .map((pair) => pair.filevine_entity!),
    [pairsData],
  );

  // Extract Finch entities from pairs
  const finchEntities = React.useMemo(
    () =>
      (pairsData?.pairs ?? [])
        .filter((pair) => pair.finch_entity != null)
        .map((pair) => pair.finch_entity!),
    [pairsData],
  );

  const handleRetry = useCallback(() => {
    refetchFieldConfig();
    refetchItems();
  }, [refetchFieldConfig, refetchItems]);

  // Check for 404 errors (not configured for this entity type)
  const isNotConfigured =
    (fieldConfigError &&
      'status' in fieldConfigError &&
      fieldConfigError.status === 404) ||
    (itemsError && 'status' in itemsError && itemsError.status === 404);

  if (isNotConfigured) {
    return <NotConfiguredState />;
  }

  // Other errors
  if (fieldConfigError || itemsError) {
    const errorMessage =
      fieldConfigError && 'data' in fieldConfigError
        ? String(
            (fieldConfigError as { data?: { error?: string } }).data?.error ||
              'Unknown error',
          )
        : itemsError && 'data' in itemsError
          ? String(
              (itemsError as { data?: { error?: string } }).data?.error ||
                'Unknown error',
            )
          : 'An unexpected error occurred';

    return <ErrorState message={errorMessage} onRetry={handleRetry} />;
  }

  // Loading data
  const isLoading = fieldConfigLoading || itemsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Calculate last sync time from items
  const lastSyncTime =
    items.length > 0
      ? formatDistanceToNow(
          parseISO(
            items.reduce((latest, item) => {
              const itemTime = item.latest_snapshot?.modified_at;
              if (!itemTime) return latest;
              return !latest || itemTime > latest ? itemTime : latest;
            }, ''),
          ),
          { addSuffix: true },
        )
      : null;

  const defaultSectionName =
    entityType === ENTITY_SYNC_TYPES.COVERAGE
      ? 'Coverage'
      : 'Medical Providers';

  return (
    <div className="space-y-6">
      {/* Section header with pipeline button */}
      <SectionHeader
        sectionName={fieldConfig?.name || defaultSectionName}
        itemCount={items.length}
        lastSyncTime={lastSyncTime}
        matterId={matterId}
        entityType={entityType}
        onPipelineComplete={handleRetry}
      />

      {/* Entity Linking Section */}
      <div className="border-t border-zinc-200 pt-6">
        <EntityLinkingSection
          matterId={matterId}
          firmId={firmId}
          canonicalSectionType={entityType}
          fieldConfigs={fieldConfig?.field_configs || []}
          sectionConfigData={fieldConfig?.data}
        />
      </div>

      {/* Sync Suggestions Card List */}
      <div className="border-t border-zinc-200 pt-6">
        <PollingProvider>
          <SyncSuggestionCardList
            matterId={matterId}
            entityType={entityType}
            canonicalSectionType={entityType}
            firmId={firmId}
            finchEntities={finchEntities}
            filevineEntities={items}
            fieldConfigs={fieldConfig?.field_configs || []}
            sectionConfigData={fieldConfig?.data}
          />
        </PollingProvider>
      </div>
    </div>
  );
};

/**
 * Main CMS Sync Tab component
 */
const CMSSyncTab: React.FC = () => {
  const { selectedMatterData, selectedMatterLoading } = useMatterSelection();
  const matterId = selectedMatterData?.id;
  const [activeTab, setActiveTab] = useState<string>(
    ENTITY_SYNC_TYPES.MEDICAL_PROVIDERS,
  );

  // Loading state
  if (selectedMatterLoading) {
    return <TabSkeleton />;
  }

  // No matter selected
  if (!matterId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500">No matter selected</p>
          <p className="text-sm text-gray-400 mt-2">
            Please select a matter to sync CMS data
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-zinc-200 p-6">
        <h1 className="text-2xl font-semibold text-zinc-900">CMS Sync</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Sync and manage Filevine entity data for this matter
        </p>
      </div>

      {/* Tabs and Content */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex-1 flex flex-col min-h-0"
      >
        <div className="border-b border-zinc-200 px-6 pt-2">
          <TabsList>
            {ENTITY_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {ENTITY_TABS.map((tab) => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className="flex-1 overflow-y-auto p-6"
          >
            <EntitySyncContent
              matterId={matterId}
              firmId={selectedMatterData?.firm?.id}
              entityType={tab.value}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default CMSSyncTab;
